/**
 * Stale-build self-healing.
 *
 * After a deploy, hashed chunks of the build a tab is running may no longer
 * exist on the server (rolling-update window, or a tab older than the asset
 * attic). A single reload picks up the current build via the no-cache
 * index.html. The sessionStorage cooldown stops reload storms while a CDN
 * edge still holds a negative-cached 404 for a chunk (Cloudflare pins asset
 * 404s for ~5 minutes — incident 2026-07-22): worst case one auto-reload per
 * cooldown, after that RouteErrorComponent's manual-reload UI takes over.
 *
 * **A failed fetch is not proof of a stale build** (KAN-196). Chunk loads also
 * fail for entirely transient reasons — a laptop waking up, a Wi-Fi switch, one
 * of the ~2 requests/minute Cloudflare aborts against our origin. Treating
 * those as "new deploy" hard-reloaded users mid-work with no deploy in sight,
 * which is what they reported. So the unverified reload
 * (``reloadOnceForStaleBuild``) is now reserved for callers that already
 * *know* the build differs; everyone reacting to a mere failure goes through
 * ``reloadIfServerBuildDiffers``, which asks ``/version.json`` first and does
 * nothing when the answer is "same build" or "don't know".
 */
import { serverBuildDiffers } from '@/lib/build-version';
import { stampReloadReason, type PageReloadReason } from '@/lib/telemetry/page-reload';

const RELOAD_STAMP_KEY = '9brains.stale-build-reload-at';
const RELOAD_COOLDOWN_MS = 60_000;

/**
 * Reload to pick up the current build, at most once per cooldown window.
 * Returns whether a reload was actually triggered.
 *
 * Unverified: only for callers that already established a newer build is live.
 * Everything reacting to a *failure* belongs in ``reloadIfServerBuildDiffers``.
 */
export function reloadOnceForStaleBuild(
  reason: PageReloadReason,
  extra?: { detail?: string; serverBuildId?: string; bypassCooldown?: boolean }
): boolean {
  let lastReloadAt = 0;
  try {
    lastReloadAt = Number(sessionStorage.getItem(RELOAD_STAMP_KEY) ?? '0');
  } catch {
    // Storage unavailable — fall through and reload unguarded.
  }
  const now = Date.now();
  // ``bypassCooldown`` is for callers whose reload is a correctness guarantee
  // rather than cosmetic self-healing (the cross-user bfcache guard). The
  // cooldown exists to damp reload storms against a 404'ing chunk; letting it
  // also silently veto a security-relevant reload would be the wrong trade,
  // and those callers fire once per event anyway, so there is no storm to damp.
  if (
    !extra?.bypassCooldown &&
    Number.isFinite(lastReloadAt) &&
    now - lastReloadAt < RELOAD_COOLDOWN_MS
  ) {
    return false;
  }
  try {
    sessionStorage.setItem(RELOAD_STAMP_KEY, String(now));
  } catch {
    // Storage unavailable — the cooldown simply won't stick.
  }
  // Stamp last: the next load reports it, so it must survive the navigation
  // but must not be written for a reload the cooldown just declined.
  stampReloadReason(reason, extra);
  window.location.reload();
  return true;
}

/**
 * Reload **only if** the server is positively serving a different build.
 *
 * The verified counterpart to the above, for callers whose trigger is a failed
 * asset load. Resolves to false when the build matches or cannot be determined
 * — in both cases the failure was transient and the caller should retry rather
 * than throw the user's page away.
 */
export async function reloadIfServerBuildDiffers(
  reason: PageReloadReason,
  extra?: { detail?: string }
): Promise<boolean> {
  if (!(await serverBuildDiffers())) return false;
  return reloadOnceForStaleBuild(reason, extra);
}

/**
 * Vite fires `vite:preloadError` when fetching a hashed chunk (module or CSS)
 * fails — including for the router's speculative on-hover preloads.
 *
 * We deliberately do **not** cancel the event and do **not** reload here. Left
 * uncancelled, Vite rethrows and the failure lands where it can be judged in
 * context: a speculative preload is swallowed by the router (it retries for
 * real when the user actually navigates), while a failure during a real
 * navigation reaches ``RouteErrorComponent``, which verifies the build before
 * deciding between reload and retry. Cancelling instead would resolve the
 * import to ``undefined`` and hand the router a broken route with no error at
 * all.
 */
export function initStaleBuildReload(): void {
  window.addEventListener('vite:preloadError', ((event: Event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    const message = payload instanceof Error ? payload.message : String(payload ?? '');
    // Visible in the console only — a preload failing is not user-facing.
    console.warn('[stale-build] chunk preload failed, letting the router retry:', message);
  }) as EventListener);
}
